import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MessageCircle, Music, BookOpen, Globe, Users, Shield, ChevronRight } from "lucide-react";
import { Footer } from "../components/Footer";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_8cb5d32c-b1ce-4951-b77e-897f98e6cc9f/artifacts/uyctypm7_41ed1e03-8bc9-42a0-8314-b43b52fec9a9.JPG";

const FORUM_CATEGORIES = [
  {
    id: "diaspora-supporters",
    title: "Diaspora Supporters",
    icon: Users,
    color: "#FBBF24",
    description: "Share why you support your club, how your football journey started, and what your team means to you as an African or diaspora supporter.",
    prompt: 'One question local British fans often ask is: "Why do you support this club?" Afro Fanthem gives diaspora fans a place to answer that question properly.',
    topics: [
      "Why I support my club",
      "How I discovered Premier League football",
      "The African player who connected me to my club",
      "Supporting from Africa vs supporting from the UK",
      "My family, my country, and my club",
      "What British fans misunderstand about African supporters"
    ]
  },
  {
    id: "music-chants-anthems",
    title: "Music, Chants and Anthems",
    icon: Music,
    color: "#10B981",
    description: "Discuss Afro Fanthem tracks, club anthems, chants, music ideas, lyrics, reactions, and requests for future songs.",
    topics: [
      "Which club deserves the next anthem?",
      "Best Afro Fanthem track so far",
      "Suggest a chant",
      "React to African Gunner Anthem",
      "React to Magpies Assembly",
      "What makes a football anthem powerful?"
    ]
  },
  {
    id: "fan-stories",
    title: "Fan Stories",
    icon: BookOpen,
    color: "#3B82F6",
    description: "Share your football journey, your first match memory, your club story, and the moments that made you a lifelong supporter.",
    topics: [
      "Why I support this team",
      "My first Premier League memory",
      "The African player who changed everything",
      "My matchday ritual from Africa or the diaspora",
      "The club moment I will never forget"
    ]
  },
  {
    id: "afcon-african-football",
    title: "AFCON / African Football",
    icon: Globe,
    color: "#F97316",
    description: "Discuss AFCON, African clubs, African players, local leagues, continental competitions, and the relationship between African football and global fandom.",
    topics: [
      "AFCON memories",
      "Which African national team has the loudest fans?",
      "African players in the Premier League",
      "Local clubs and global clubs",
      "What European clubs can learn from African football culture"
    ]
  }
];

const CLUB_HUBS = [
  { name: "Arsenal", slug: "arsenal" },
  { name: "Manchester United", slug: "manchester-united" },
  { name: "Liverpool", slug: "liverpool" },
  { name: "Chelsea", slug: "chelsea" },
  { name: "Newcastle United", slug: "newcastle-united" },
  { name: "Manchester City", slug: "manchester-city" },
  { name: "Tottenham Hotspur", slug: "tottenham-hotspur" },
  { name: "Aston Villa", slug: "aston-villa" },
  { name: "Nottingham Forest", slug: "nottingham-forest" },
  { name: "AFC Bournemouth", slug: "bournemouth" },
  { name: "Burnley", slug: "burnley" },
  { name: "Wolverhampton", slug: "wolverhampton" },
  { name: "Brighton", slug: "brighton" },
  { name: "Everton", slug: "everton" },
  { name: "Leeds United", slug: "leeds" },
  { name: "West Ham United", slug: "west-ham" },
  { name: "Crystal Palace", slug: "crystal-palace" },
  { name: "Brentford", slug: "brentford" },
  { name: "Sunderland", slug: "sunderland" },
  { name: "Fulham", slug: "fulham" }
];

export default function ForumPage() {
  const [topicsByCategory, setTopicsByCategory] = useState({});

  useEffect(() => {
    document.title = "Fan Forum - Afro Fanthem";
    window.scrollTo(0, 0);
    fetchTopics();
  }, []);

  const fetchTopics = async () => {
    try {
      const res = await fetch(`${API_URL}/api/forum/topics?limit=100`);
      if (res.ok) {
        const data = await res.json();
        const grouped = {};
        for (const t of data.topics) {
          if (!grouped[t.category_id]) grouped[t.category_id] = [];
          grouped[t.category_id].push(t);
        }
        setTopicsByCategory(grouped);
      }
    } catch {
      // topic fetch failed silently
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/60 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            <Link to="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Afro Fanthem" className="h-10 md:h-14 w-auto" />
            </Link>
            <div className="flex items-center gap-4 sm:gap-6">
              <Link to="/" className="text-gray-300 hover:text-white text-sm transition-colors">Home</Link>
              <Link to="/forum-rules" className="text-gray-300 hover:text-white text-sm transition-colors hidden sm:inline">Rules</Link>
              <Link to="/survey" className="bg-[#FBBF24] text-black font-bold uppercase tracking-widest px-4 sm:px-6 py-2 sm:py-3 text-sm hover:bg-[#F59E0B] transition-all" data-testid="forum-survey-cta">
                Take the Survey
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-28 md:pt-36 pb-12 px-4 text-center">
        <div className="max-w-4xl mx-auto">
          <p className="text-[#FBBF24] text-xs uppercase tracking-[0.3em] font-bold mb-4">From the Afro Fanthem Forum</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white uppercase tracking-tight mb-6" data-testid="forum-headline">
            Fan <span className="text-[#FBBF24]">Debate</span>
          </h1>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto leading-relaxed">
            A space for African and diaspora football fans to debate, share stories, react to music, and explain what their clubs mean to them.
          </p>
        </div>
      </section>

      {/* Access notice */}
      <section className="pb-12 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-[#141414] border border-white/10 p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <Shield className="w-6 h-6 text-[#FBBF24] flex-shrink-0 mt-1 sm:mt-0" />
            <div className="flex-1">
              <p className="text-gray-300 text-sm leading-relaxed">
                <strong className="text-white">Join the movement before joining the debate.</strong> Complete the African Football Fan Survey or register to share your voice.
              </p>
            </div>
            <div className="flex gap-3 flex-shrink-0">
              <Link to="/forum/join" className="bg-[#FBBF24] text-black font-bold uppercase tracking-widest px-4 py-2 text-xs hover:bg-[#F59E0B] transition-all" data-testid="forum-access-survey-cta">
                Get Forum Access
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Forum Categories */}
      <section className="pb-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="space-y-8">
            {FORUM_CATEGORIES.map((category) => (
              <div key={category.id} className="bg-[#141414] border border-white/10 overflow-hidden" data-testid={`forum-category-${category.id}`}>
                <div className="p-6 sm:p-8 border-b border-white/10">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${category.color}15` }}>
                      <category.icon className="w-6 h-6" style={{ color: category.color }} />
                    </div>
                    <div className="flex-1">
                      <h2 className="font-heading text-2xl sm:text-3xl text-white uppercase tracking-tight mb-2">{category.title}</h2>
                      <p className="text-gray-400 leading-relaxed">{category.description}</p>
                      {category.prompt && (
                        <p className="text-gray-300 italic mt-3 text-sm border-l-2 pl-4" style={{ borderColor: category.color }}>
                          {category.prompt}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                <div className="divide-y divide-white/5">
                  {(topicsByCategory[category.id] || category.topics.map((t, i) => ({ topic_id: `static_${i}`, title: t, comment_count: 0 }))).map((topic, idx) => {
                    const title = typeof topic === "string" ? topic : topic.title;
                    const topicId = typeof topic === "string" ? null : topic.topic_id;
                    const commentCount = typeof topic === "string" ? 0 : topic.comment_count;
                    const inner = (
                      <div className="px-6 sm:px-8 py-4 flex items-center justify-between gap-4 hover:bg-white/[0.02] transition-colors group">
                        <div className="flex items-center gap-3 min-w-0">
                          <MessageCircle className="w-4 h-4 text-gray-600 flex-shrink-0" />
                          <span className="text-gray-300 group-hover:text-white transition-colors truncate">{title}</span>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          {commentCount > 0 && <span className="text-gray-600 text-xs">{commentCount}</span>}
                          <ChevronRight className="w-4 h-4 text-gray-600 group-hover:text-[#FBBF24] transition-colors" />
                        </div>
                      </div>
                    );
                    return topicId ? (
                      <Link key={topicId} to={`/forum/topic/${topicId}`} className="block">{inner}</Link>
                    ) : (
                      <div key={title}>{inner}</div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Club Hubs Preview */}
      <section className="py-16 bg-[#141414] border-t border-white/10 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-[#FBBF24] text-xs uppercase tracking-[0.3em] font-bold mb-4">Find Your People</p>
            <h2 className="font-heading text-3xl sm:text-4xl text-white uppercase tracking-tight mb-4" data-testid="club-hubs-headline">Club Hubs</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Connect with African and diaspora fans of your club. Share stories, debate, and celebrate the music of your team.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {CLUB_HUBS.map((club) => (
              <Link
                key={club.slug}
                to={`/forum/club/${club.slug}`}
                className="bg-white/5 border border-white/10 p-6 text-center hover:border-[#FBBF24]/50 hover:bg-[#FBBF24]/5 transition-all group"
                data-testid={`club-hub-${club.slug}`}
              >
                <h3 className="font-heading text-lg text-white uppercase group-hover:text-[#FBBF24] transition-colors">{club.name}</h3>
                <p className="text-gray-500 text-xs mt-2 uppercase tracking-wider">Africa Fan Hub</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Survey CTA */}
      <section className="py-16 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-heading text-3xl sm:text-4xl text-white uppercase tracking-tight mb-6">
            Make Your Voice <span className="text-[#FBBF24]">Count</span>
          </h2>
          <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
            Complete the African Football Fan Survey to unlock commenting and join the debate. Your response adds to the evidence that African fandom matters.
          </p>
          <Link to="/survey" className="inline-flex items-center gap-2 bg-[#FBBF24] text-black font-bold uppercase tracking-widest px-8 py-4 hover:bg-[#F59E0B] transition-all hover:-translate-y-1" data-testid="forum-bottom-survey-cta">
            Take the Survey
            <ArrowRight className="w-5 h-5" />
          </Link>
          <p className="mt-6">
            <Link to="/forum-rules" className="text-gray-500 hover:text-gray-300 text-sm transition-colors">Read the Forum Rules</Link>
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
